// =============================================================================
// VandieZ agent-web -- Load detail + bid placement (/marketplace/:loadId)
// =============================================================================
// Shows a load's full details and lets the agent place a bid on behalf of
// the currently-selected owner.
//
// TODO(v2): Replace the driver UUID free-text field with a full driver picker
// that lists drivers associated with the owner's fleet.
//
// Adapted from shipper-web patterns:
//   * datetime-local → ISO conversion uses +05:30 IST offset trick
//     (PostLoadPage.tsx line 98).
//   * Vehicle picker filters client-side for active + online + matching type
//     and payload.
// =============================================================================

import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Loader2,
  MapPin,
  Package,
} from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { bidsApi, loadsApi, vehiclesApi } from "@/api/client";
import { AppHeader } from "@/components/AppHeader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { useOwnerContext } from "@/lib/owner-context";
import { ApiError } from "@/types/auth";
import type { BidPlacePayload } from "@/types/bid";
import type { Load } from "@/types/load";
import type { Vehicle } from "@/types/vehicle";

// Convert a datetime-local string (YYYY-MM-DDTHH:MM) to ISO 8601, treating
// the input as IST (UTC+05:30) to match what the backend expects.
function istLocalToISO(localDt: string): string {
  return new Date(`${localDt}:00+05:30`).toISOString();
}

function formatINR(amount: string): string {
  const n = Number.parseFloat(amount);
  if (Number.isNaN(n)) {return amount;}
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// Convert ISO to datetime-local input value (IST)
function isoToLocalInput(iso: string): string {
  try {
    const d = new Date(iso);
    // Offset by +5:30
    const istMs = d.getTime() + (5 * 60 + 30) * 60_000;
    return new Date(istMs).toISOString().slice(0, 16);
  } catch {
    return "";
  }
}

export function LoadBidPage(): React.JSX.Element {
  const { loadId } = useParams<{ loadId: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { currentOwnerId, currentOwner } = useOwnerContext();

  const [load, setLoad] = useState<Load | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoadingLoad, setIsLoadingLoad] = useState(true);

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(true);

  // Bid form state
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [driverUuid, setDriverUuid] = useState("");
  const [bidAmount, setBidAmount] = useState("");
  const [proposedEta, setProposedEta] = useState("");
  const [validityHours, setValidityHours] = useState("6");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (accessToken === null || currentOwnerId === null || loadId === undefined) {
      setIsLoadingLoad(false);
      return;
    }
    const controller = new AbortController();
    setIsLoadingLoad(true);
    loadsApi
      .detail(accessToken, loadId, currentOwnerId, controller.signal)
      .then((res) => {
        setLoad(res);
        setIsLoadingLoad(false);
        // Pre-fill eta with pickup_window_start
        setProposedEta(isoToLocalInput(res.pickup_window_start));
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) {return;}
        if (err instanceof ApiError) {
          const code = err.problem.code;
          if (err.problem.status === 403 && code === "AGENT_PERMISSION_MISSING") {
            setLoadError("Ask the owner to grant the 'loads:read' permission on 'loads'.");
          } else if (err.problem.status === 403 && code === "AGENT_NO_GRANT_FOR_OWNER") {
            setLoadError("Your access to this owner was revoked. Switch owner via the header dropdown.");
          } else {
            setLoadError(err.problem.detail);
          }
        } else {
          setLoadError("Couldn't load this load.");
        }
        setIsLoadingLoad(false);
      });
    return () => { controller.abort(); };
  }, [accessToken, currentOwnerId, loadId]);

  useEffect(() => {
    if (accessToken === null || currentOwnerId === null) {
      setIsLoadingVehicles(false);
      return;
    }
    const controller = new AbortController();
    setIsLoadingVehicles(true);
    vehiclesApi
      .list(accessToken, currentOwnerId, { limit: 100 }, controller.signal)
      .then((res) => {
        setVehicles(res.items);
        setIsLoadingVehicles(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) {return;}
        // Non-fatal — just means vehicle picker will be empty
        if (err instanceof ApiError) {
          // Swallow silently; load bid form can still show the load detail
        }
        setIsLoadingVehicles(false);
      });
    return () => { controller.abort(); };
  }, [accessToken, currentOwnerId]);

  async function onSubmitBid(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setFormError(null);

    if (accessToken === null || currentOwnerId === null || load === null || loadId === undefined) {
      return;
    }

    // Validation
    const amount = Number.parseFloat(bidAmount);
    const budgetMin = Number.parseFloat(load.budget_min);
    const budgetMax = Number.parseFloat(load.budget_max);

    if (!selectedVehicleId) {
      setFormError("Please select a vehicle.");
      return;
    }
    if (!driverUuid.trim()) {
      setFormError("Please enter the driver UUID.");
      return;
    }
    if (Number.isNaN(amount)) {
      setFormError("Please enter a valid bid amount.");
      return;
    }
    if (load.budget_visible_to_bidders && (amount < budgetMin || amount > budgetMax)) {
      setFormError(
        `Bid amount must be between ${formatINR(load.budget_min)} and ${formatINR(load.budget_max)}.`,
      );
      return;
    }
    if (!proposedEta) {
      setFormError("Please enter the proposed ETA for pickup.");
      return;
    }

    const hrs = Number.parseInt(validityHours, 10);
    if (Number.isNaN(hrs) || hrs < 1 || hrs > 24) {
      setFormError("Bid validity must be between 1 and 24 hours.");
      return;
    }

    const payload: BidPlacePayload = {
      vehicle_id: selectedVehicleId,
      proposed_driver_id: driverUuid.trim(),
      bid_amount: amount,
      proposed_eta_pickup: istLocalToISO(proposedEta),
      ...(hrs !== 6 ? { bid_validity_hours: hrs } : {}),
      ...(message.trim().length > 0 ? { message_to_shipper: message.trim() } : {}),
    };

    setSubmitting(true);
    setSubmitError(null);
    try {
      await bidsApi.place(accessToken, loadId, currentOwnerId, payload);
      navigate(`/bids?as=${currentOwnerId}`);
    } catch (err) {
      if (err instanceof ApiError) {
        const code = err.problem.code;
        if (err.problem.status === 403 && code === "AGENT_PERMISSION_MISSING") {
          setSubmitError("Ask the owner to grant the 'bids:place' permission.");
        } else if (err.problem.status === 403 && code === "AGENT_NO_GRANT_FOR_OWNER") {
          setSubmitError("Your access to this owner was revoked. Switch owner via the header dropdown.");
        } else {
          setSubmitError(err.problem.detail);
        }
      } else {
        setSubmitError("Couldn't place the bid. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (currentOwnerId === null) {
    return (
      <div className="min-h-screen bg-muted/30">
        <AppHeader title="Load detail" />
        <main className="mx-auto max-w-4xl px-6 py-16 text-center text-muted-foreground">
          No owner selected — head to{" "}
          <Link to="/" className="font-medium text-primary underline underline-offset-2">
            My Owners
          </Link>{" "}
          and pick one.
        </main>
      </div>
    );
  }

  if (isLoadingLoad) {
    return (
      <div className="min-h-screen bg-muted/30">
        <AppHeader title="Load detail" />
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (load === null) {
    return (
      <div className="min-h-screen bg-muted/30">
        <AppHeader title="Load detail" />
        <div className="mx-auto max-w-3xl px-6 py-10">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{loadError ?? "Load not found."}</AlertDescription>
          </Alert>
          <Link to="/marketplace" className="mt-4 inline-block">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4" />
              Back to marketplace
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Filter vehicles that match this load's requirements
  const eligibleVehicles = vehicles.filter(
    (v) =>
      v.status === "active" &&
      v.online_for_bidding &&
      v.payload_capacity_kg >= load.required_payload_kg &&
      v.vehicle_type === load.required_vehicle_type,
  );

  const ownerName = currentOwner?.owner_full_name ?? "this owner";

  return (
    <div className="min-h-screen bg-muted/30 pb-16">
      <AppHeader title="Load detail" />

      <main className="mx-auto max-w-4xl space-y-6 px-6 py-8">
        <div>
          <Link to="/marketplace">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4" />
              Back to marketplace
            </Button>
          </Link>
        </div>

        {/* Load detail card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-lg">
                {load.pickup_district} → {load.drop_district}
              </CardTitle>
            </div>
            <CardDescription className="font-mono text-xs">{load.id}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="mb-1 text-xs uppercase tracking-widest text-muted-foreground">
                  Pickup
                </p>
                <div className="flex items-start gap-1">
                  <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
                  <div>
                    <p className="text-sm">{load.pickup_address}</p>
                    <p className="text-xs text-muted-foreground">
                      Window: {formatDateTime(load.pickup_window_start)} –{" "}
                      {formatDateTime(load.pickup_window_end)}
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <p className="mb-1 text-xs uppercase tracking-widest text-muted-foreground">
                  Drop
                </p>
                <div className="flex items-start gap-1">
                  <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-danger" />
                  <p className="text-sm">{load.drop_address}</p>
                </div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <StatItem label="Material" value={load.material_category} />
              <StatItem label="Weight" value={`${load.weight_kg} kg`} />
              <StatItem label="Vehicle type" value={load.required_vehicle_type} />
              <StatItem label="Distance" value={`${load.estimated_distance_km} km`} />
              {load.budget_visible_to_bidders ? (
                <StatItem
                  label="Budget range"
                  value={`${formatINR(load.budget_min)} – ${formatINR(load.budget_max)}`}
                />
              ) : (
                <StatItem label="Budget" value="Hidden" />
              )}
              <div className="flex items-center gap-1">
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">
                    Bid window closes
                  </p>
                  <p className="flex items-center gap-1 text-sm font-medium">
                    {/* Deliberately the subtle pair's dark foreground, not the
                        solid --warning fill: solid amber is only 2.15:1 on a
                        white card, below even the 3:1 floor for meaningful
                        non-text. This keeps the amber hue at 9.22:1. */}
                    <Clock className="h-3 w-3 text-warning-subtle-foreground" />
                    {formatDateTime(load.bid_window_ends_at)}
                  </p>
                </div>
              </div>
            </div>

            {load.material_description !== null ? (
              <div>
                <p className="mb-1 text-xs uppercase tracking-widest text-muted-foreground">
                  Material description
                </p>
                <p className="text-sm">{load.material_description}</p>
              </div>
            ) : null}

            {load.special_requirements.length > 0 ? (
              <div>
                <p className="mb-1 text-xs uppercase tracking-widest text-muted-foreground">
                  Special requirements
                </p>
                <p className="text-sm">{load.special_requirements.join(", ")}</p>
              </div>
            ) : null}
          </CardContent>
        </Card>

        {/* Bid form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Place a bid for {ownerName}
            </CardTitle>
            <CardDescription>
              Submit a bid on this load on behalf of the selected owner.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => { void onSubmitBid(e); }}
              className="space-y-5"
            >
              {/* Vehicle picker */}
              <div className="space-y-2">
                <Label htmlFor="vehicle-select">Vehicle</Label>
                {isLoadingVehicles ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading vehicles…
                  </div>
                ) : eligibleVehicles.length === 0 ? (
                  <Alert>
                    <AlertDescription>
                      None of {ownerName}'s vehicles match this load's requirements
                      (type: {load.required_vehicle_type}, min payload:{" "}
                      {load.required_payload_kg} kg, must be active + online for bidding).{" "}
                      <Link to="/vehicles" className="font-medium text-primary underline underline-offset-2">
                        View fleet
                      </Link>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <select
                    id="vehicle-select"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    value={selectedVehicleId}
                    onChange={(e) => { setSelectedVehicleId(e.target.value); }}
                    required
                  >
                    <option value="">Select a vehicle…</option>
                    {eligibleVehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.make} {v.model} · {v.registration_number} ·{" "}
                        {v.payload_capacity_kg} kg
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Driver UUID */}
              <div className="space-y-2">
                <Label htmlFor="driver-uuid">Driver UUID</Label>
                <Input
                  id="driver-uuid"
                  placeholder="Paste the driver's UUID (e.g. 018f2a3b-…)"
                  value={driverUuid}
                  onChange={(e) => { setDriverUuid(e.target.value); }}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Paste the driver UUID — full driver picker coming in v2.
                </p>
              </div>

              {/* Bid amount */}
              <div className="space-y-2">
                <Label htmlFor="bid-amount">
                  Bid amount (₹)
                  {load.budget_visible_to_bidders ? (
                    <span className="ml-2 font-normal text-muted-foreground">
                      Budget: {formatINR(load.budget_min)} – {formatINR(load.budget_max)}
                    </span>
                  ) : null}
                </Label>
                <Input
                  id="bid-amount"
                  inputMode="numeric"
                  placeholder="e.g. 8500"
                  value={bidAmount}
                  onChange={(e) => { setBidAmount(e.target.value.replace(/[^\d.]/g, "")); }}
                  required
                />
              </div>

              {/* Proposed ETA pickup */}
              <div className="space-y-2">
                <Label htmlFor="eta-pickup">Proposed pickup ETA (IST)</Label>
                <Input
                  id="eta-pickup"
                  type="datetime-local"
                  value={proposedEta}
                  min={isoToLocalInput(load.pickup_window_start)}
                  max={isoToLocalInput(load.pickup_window_end)}
                  onChange={(e) => { setProposedEta(e.target.value); }}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Must be within the shipper's pickup window:{" "}
                  {formatDateTime(load.pickup_window_start)} –{" "}
                  {formatDateTime(load.pickup_window_end)}
                </p>
              </div>

              {/* Bid validity hours */}
              <div className="space-y-2">
                <Label htmlFor="validity-hours">Bid validity (hours, 1–24)</Label>
                <Input
                  id="validity-hours"
                  inputMode="numeric"
                  min={1}
                  max={24}
                  value={validityHours}
                  onChange={(e) => { setValidityHours(e.target.value.replace(/[^\d]/g, "")); }}
                />
              </div>

              {/* Optional message */}
              <div className="space-y-2">
                <Label htmlFor="message">
                  Message to shipper{" "}
                  <span className="font-normal text-muted-foreground">(optional)</span>
                </Label>
                <Textarea
                  id="message"
                  placeholder="Any notes for the shipper…"
                  maxLength={500}
                  value={message}
                  onChange={(e) => { setMessage(e.target.value); }}
                  rows={3}
                />
                <p className="text-right text-xs text-muted-foreground">
                  {message.length}/500
                </p>
              </div>

              {formError !== null ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{formError}</AlertDescription>
                </Alert>
              ) : null}

              {submitError !== null ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              ) : null}

              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={submitting || eligibleVehicles.length === 0}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Placing bid…
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      Place bid
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function StatItem({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="text-sm font-medium">{value}</p>
    </div>
  );
}
